import { createContext, use } from 'react';

import type { AdminCapability, AdminRole } from '@/lib/permissions';

export interface Operator {
  id: string;
  name: string;
  email: string;
  role: AdminRole;
  /** Resolved server-side from the role matrix; never computed from the role here. */
  capabilities: readonly AdminCapability[];
  avatarUrl?: string;
}

export interface ImpersonationState {
  /** The user being viewed. Read-only, fully audited, unmistakably banner-flagged. */
  userId: string;
  username: string;
  startedAt: string;
}

export interface OperatorContextValue {
  operator: Operator;
  impersonating: ImpersonationState | null;
  /**
   * Capability check for rendering affordances. This hides doors; it does not
   * lock them. mesh-admin-api re-checks every capability on every request.
   */
  can: (capability: AdminCapability) => boolean;
  stopImpersonating: () => void;
}

export const OperatorContext = createContext<OperatorContextValue | null>(null);

export function useOperator(): OperatorContextValue {
  const value = use(OperatorContext);
  if (!value) {
    throw new Error('useOperator must be used inside <OperatorProvider>');
  }
  return value;
}

/** Initials for the top-bar avatar, e.g. "Jesse McMahon" → "JM". */
export function operatorInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
